import {DatePicker} from '@github-ui/date-picker'
import type {
  IssueFieldSelection,
  IssueFieldSelectionValues,
  IssueFieldSingleSelectOption,
} from '@github-ui/issue-fields-shared/issue-field'
import {numberValidation} from '@github-ui/issue-metadata/IssueFieldNumberEditor'
import {IssueFieldTextEditorInternal} from '@github-ui/issue-metadata/IssueFieldTextEditor'
import {
  type IssueFieldWithUnsavedValue,
  toIssueFieldWithUnsavedValue,
} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import {IssueSingleSelectFieldPicker} from '@github-ui/item-picker/IssueSingleSelectFieldPicker'
import type {ButtonProps} from '@primer/react'
import {useCallback, useMemo, useState} from 'react'

import {IssueFieldValueSharedEditorAnchor} from './IssueFieldValueSharedEditorAnchor'

const FALLBACK_WIDTH = '256px'
const NORMALIZED_HOUR = 12

export type IssueFieldValueSharedEditorProps = {
  field: IssueFieldWithUnsavedValue
  onChange: (field: IssueFieldWithUnsavedValue, value: IssueFieldSelectionValues) => void
  className?: string
  readonly?: boolean
}

export type IssueFieldValueSharedPendingEditorProps = {
  pendingField: IssueFieldSelection
  onCommit: (fieldId: string, fieldValue: IssueFieldSelectionValues) => void
  onClose: () => void
}

export const IssueFieldValueSharedPendingEditor = ({
  pendingField,
  onCommit,
  onClose,
}: IssueFieldValueSharedPendingEditorProps) => {
  return IssueFieldEditor(toIssueFieldWithUnsavedValue(pendingField, null), onCommit, onClose, true)
}

export const IssueFieldValueSharedEditor = ({
  field,
  onChange,
  className,
  readonly,
}: IssueFieldValueSharedEditorProps) => {
  const onCommit = useCallback(
    (fieldId: string, value: IssueFieldSelectionValues) => {
      onChange(field, value)
    },
    [onChange, field],
  )
  if (!field.field.id || !field.field.name) return null

  return IssueFieldEditor(field, onCommit, undefined, false, className, readonly)
}

const IssueFieldNumberEditor = (
  field: IssueFieldWithUnsavedValue,
  onCommit: (fieldId: string, value: IssueFieldSelectionValues) => void,
  anchorElement: (buttonProps: ButtonProps | undefined) => React.JSX.Element,
  shouldOpen: boolean = false,
  onClose?: () => void,
) => {
  return (
    <IssueFieldTextEditorInternal
      fieldId={field.field.id!}
      fieldName={field.field.name!}
      initialValue={field.value as string}
      onCommit={onCommit}
      renderAnchor={anchorElement}
      overrideWidth={FALLBACK_WIDTH}
      isNumber
      validate={numberValidation}
      shouldOpen={shouldOpen}
      onClose={onClose}
    />
  )
}

const IssueFieldTextEditor = (
  field: IssueFieldWithUnsavedValue,
  onCommit: (fieldId: string, value: IssueFieldSelectionValues) => void,
  anchorElement: (buttonProps: ButtonProps | undefined) => React.JSX.Element,
  shouldOpen: boolean = false,
  onClose?: () => void,
) => {
  return (
    <IssueFieldTextEditorInternal
      fieldId={field.field.id!}
      fieldName={field.field.name!}
      initialValue={field.value as string}
      onCommit={onCommit}
      renderAnchor={anchorElement}
      overrideWidth={FALLBACK_WIDTH}
      shouldOpen={shouldOpen}
      onClose={onClose}
    />
  )
}

const IssueFieldDateEditor = (
  field: IssueFieldWithUnsavedValue,
  onCommit: (fieldId: string, value: IssueFieldSelectionValues) => void,
  anchorElement: (buttonProps: ButtonProps | undefined) => React.JSX.Element,
  shouldOpen: boolean = false,
  onClose?: () => void,
) => {
  const [open, setOpen] = useState(shouldOpen)

  const datePickerValue = useMemo(() => {
    const value = field.value as Date | null
    if (!value) return null
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate())
  }, [field.value])

  const handleDateChange = useCallback(
    (date: Date | null) => {
      let normalizedDate: Date | null = null
      if (date) {
        normalizedDate = new Date(
          Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), NORMALIZED_HOUR, 0, 0, 0),
        )
      }
      onCommit(field.field.id!, normalizedDate)
    },
    [field.field.id, onCommit],
  )

  return (
    <DatePicker
      key={field.field.id}
      anchor={anchorProps => anchorElement(anchorProps)}
      showClearButton
      showTodayButton
      variant="single"
      dateFormat="long"
      onChange={handleDateChange}
      value={datePickerValue}
      placeholder="Set date"
      open={open}
      onOpen={() => {
        setOpen(true)
      }}
      onClose={() => {
        setOpen(false)
        onClose?.()
      }}
      anchoredOverlayProps={{
        side: 'outside-top',
      }}
    />
  )
}

const IssueFieldSingleSelectEditor = (
  field: IssueFieldWithUnsavedValue,
  onCommit: (fieldId: string, value: IssueFieldSelectionValues) => void,
  anchorElement: (buttonProps: ButtonProps | undefined) => React.JSX.Element,
  shouldOpen: boolean = false,
  onClose?: () => void,
) => {
  const ssFieldValue = field.value as IssueFieldSingleSelectOption | null
  return (
    <IssueSingleSelectFieldPicker
      key={field.field.id}
      fieldId={field.field.id!}
      fieldName={field.field.name || ''}
      selectedOption={ssFieldValue?.name || null}
      onSelectionChange={(selectedOption: IssueFieldSelectionValues) => {
        onCommit(field.field.id!, selectedOption)
      }}
      anchorElement={anchorProps => anchorElement(anchorProps)}
      readonly={false}
      isLazy
      shouldOpen={shouldOpen}
      onClose={onClose}
      insidePortal
    />
  )
}

const IssueFieldEditor = (
  field: IssueFieldWithUnsavedValue,
  onCommit: (fieldId: string, value: IssueFieldSelectionValues) => void,
  onClose?: () => void,
  shouldOpen: boolean = false,
  className?: string,
  readonly?: boolean,
) => {
  const anchorElement = (buttonProps: ButtonProps | undefined) => (
    <IssueFieldValueSharedEditorAnchor
      key={field.field.id}
      fieldWithValue={field}
      buttonProps={buttonProps}
      className={className}
      readonly={readonly}
    />
  )
  switch (field.field.dataType) {
    case 'SINGLE_SELECT':
      return IssueFieldSingleSelectEditor(field, onCommit, anchorElement, shouldOpen, onClose)
    case 'DATE':
      return IssueFieldDateEditor(field, onCommit, anchorElement, shouldOpen, onClose)
    case 'TEXT':
      return IssueFieldTextEditor(field, onCommit, anchorElement, shouldOpen, onClose)
    case 'NUMBER':
      return IssueFieldNumberEditor(field, onCommit, anchorElement, shouldOpen, onClose)
  }
}
